import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { ErrorNotice, useAction } from "../components/ui";
import { api, type Me, type Reminder, type TodayList } from "../lib/api";
import { addDays, friendlyDate } from "../lib/dates";
import { formatDay } from "../lib/format";
import { DeskSections } from "./DeskSections";

const pad = (n: number) => String(n).padStart(2, "0");

/** The daily desk: who to chase, overdue first. */
export function Today({ me }: { me: Me }) {
  const [scope, setScope] = useState<"mine" | "team">("mine");
  const [list, setList] = useState<TodayList | null>(null);
  const load = useAction();
  const act = useAction();

  const reload = useCallback(
    () => load.run(async () => setList(await api.today(scope))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scope],
  );
  useEffect(() => void reload(), [reload]);

  const update = (r: Reminder, change: { done?: boolean; dueOn?: string }) =>
    void act.run(async () => {
      await api.updateReminder(r.id, change);
      await reload();
    });

  const total = list ? list.overdue.length + list.dueToday.length : 0;

  return (
    <>
      <div className="page-head">
        <p className="label figures">{list ? `Today · ${formatDay(list.today)}` : "…"}</p>
        <h1 className="display">
          {list && total === 0 ? (
            <>
              Desk is <em>clear</em>.
            </>
          ) : (
            <>
              Who to <em>chase</em>.
            </>
          )}
        </h1>
        <p className="lede">
          {list
            ? total === 0
              ? "Nothing due. Log a call on a company to set your next follow-up."
              : `${total} to do today${list.overdue.length ? `, ${list.overdue.length} of them overdue` : ""}.`
            : "Loading your reminders…"}
        </p>
      </div>

      <div className="tabs" role="group" aria-label="Whose reminders">
        {(["mine", "team"] as const).map((s) => (
          <button key={s} type="button" className="tab" aria-pressed={scope === s} onClick={() => setScope(s)}>
            {s === "mine" ? "Mine" : `Whole team · ${me.tenant.name}`}
          </button>
        ))}
      </div>

      <ErrorNotice error={load.error ?? act.error} />

      {list && (
        <>
          <ReminderGroup title="Overdue" tone="overdue" reminders={list.overdue} today={list.today} team={scope === "team"} onUpdate={update} busy={act.pending} />
          <ReminderGroup title="Today" reminders={list.dueToday} today={list.today} team={scope === "team"} onUpdate={update} busy={act.pending} />
          <ReminderGroup title="Next 7 days" reminders={list.upcoming} today={list.today} team={scope === "team"} onUpdate={update} busy={act.pending} quiet />
        </>
      )}

      <DeskSections me={me} />
    </>
  );
}

export function ReminderGroup({
  title,
  reminders,
  today,
  team,
  onUpdate,
  busy,
  tone,
  quiet,
  showCompany = true,
}: {
  title: string;
  reminders: Reminder[];
  today: string;
  team: boolean;
  onUpdate: (r: Reminder, change: { done?: boolean; dueOn?: string }) => void;
  busy: boolean;
  tone?: "overdue";
  quiet?: boolean;
  showCompany?: boolean;
}) {
  if (quiet && reminders.length === 0) return null;
  return (
    <section className="section" aria-label={title}>
      <div className="section-head">
        <h2 className={`label ${tone === "overdue" && reminders.length ? "alert" : ""}`}>{title}</h2>
        <span className="label figures">{pad(reminders.length)}</span>
      </div>
      <ol className="index">
        {reminders.map((r, i) => (
          <li key={r.id}>
            <span className="num" aria-hidden>
              {pad(i + 1)}
            </span>
            <span className="main">
              <span className="primary">{r.note}</span>
              <span className="secondary">
                {showCompany && <Link to={`/companies/${r.company.id}`}>{r.company.name}</Link>}
                {r.contact && <>{showCompany ? " · " : ""}{r.contact.fullName}</>}
                {team && <> · {r.assignedTo.name}</>}
                {" · "}
                <span className={`figures ${r.dueOn < today ? "late" : ""}`}>{friendlyDate(r.dueOn, today)}</span>
              </span>
            </span>
            <span className="actions">
              <button type="button" className="link-btn" disabled={busy} onClick={() => onUpdate(r, { done: true })}>
                Done
              </button>
              <button
                type="button"
                className="link-btn"
                disabled={busy}
                onClick={() => onUpdate(r, { dueOn: addDays(today, 1) })}
                aria-label={`Snooze “${r.note}” to tomorrow`}
              >
                Tomorrow
              </button>
              <button
                type="button"
                className="link-btn"
                disabled={busy}
                onClick={() => onUpdate(r, { dueOn: addDays(today, 7) })}
                aria-label={`Snooze “${r.note}” by a week`}
              >
                +1 week
              </button>
            </span>
          </li>
        ))}
        {reminders.length === 0 && (
          <li>
            <span className="empty">Nothing here.</span>
          </li>
        )}
      </ol>
    </section>
  );
}
